/**
 * -----------------------------------------------------------------------------
 * Fleet Track 1.0 access after Fleet Track 2.0 registration
 * -----------------------------------------------------------------------------
 * Self-contained controls (they load and save on their own, outside the page's
 * formik form): the account-wide policy, and a per-user override.
 */

import { FC } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  useAccountFt1AccessPolicyQuery,
  useEditAccountFt1AccessPolicyMutation,
  useEditUserFt1AccessMutation,
  useUserFt1AccessQuery,
} from "../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../api/network/errorCodes";
import { TFt1AccessChoice } from "../../api/types/Admin";
import { AdminFormFieldDropdown } from "../../components/admin/formFields";

const useSaveToasts = () => {
  const { t } = useTranslation("translation", { keyPrefix: "ft1_access" });
  return {
    t,
    onError: (error: any) => {
      const errors = !!error?.data
        ? serializeErrorKeyValues(error?.data)
        : [t("toast_failed")];
      toast.error(errors?.join(" "));
    },
  };
};

export const AccountFt1AccessPolicyField: FC<{
  accountId: string;
  userCanEdit: boolean;
}> = ({ accountId, userCanEdit }) => {
  const { t, onError } = useSaveToasts();
  const { data, isFetching } = useAccountFt1AccessPolicyQuery(
    { account_id: accountId },
    { skip: !accountId }
  );
  const [savePolicy, { isLoading }] = useEditAccountFt1AccessPolicyMutation();

  const options = [
    { label: t("account_option_allow"), value: "allow" },
    { label: t("account_option_block"), value: "block" },
  ];

  return (
    <AdminFormFieldDropdown
      loadingData={isFetching}
      label={t("account_label")}
      id="allow_ft1_after_ft2_registration"
      name="allow_ft1_after_ft2_registration"
      value={data?.allow_ft1_after_ft2_registration === false ? "block" : "allow"}
      options={options}
      onChange={(e) => {
        if (!e) return;
        savePolicy({
          account_id: accountId,
          allow_ft1_after_ft2_registration: e.value === "allow",
        })
          .unwrap()
          .then((response) =>
            toast.success(
              t("toast_saved_counts", {
                blocked: response.users_blocked ?? 0,
                unblocked: response.users_unblocked ?? 0,
              })
            )
          )
          .catch(onError);
      }}
      bottomLabel={t("account_help")}
      disabled={!userCanEdit || isLoading}
      detailsFormField={true}
    />
  );
};

export const UserFt1AccessField: FC<{
  accountId: string;
  userId: string;
  userCanEdit: boolean;
}> = ({ accountId, userId, userCanEdit }) => {
  const { t, onError } = useSaveToasts();
  const { data, isFetching } = useUserFt1AccessQuery(
    { account_id: accountId, user_id: userId },
    { skip: !accountId || !userId }
  );
  const [saveAccess, { isLoading }] = useEditUserFt1AccessMutation();

  const options = [
    { label: t("user_option_account"), value: "account" },
    { label: t("user_option_allow"), value: "allow" },
    { label: t("user_option_block"), value: "block" },
  ];

  return (
    <AdminFormFieldDropdown
      loadingData={isFetching}
      label={t("user_label")}
      id="ft1_access"
      name="ft1_access"
      value={data?.ft1_access ?? "account"}
      options={options}
      onChange={(e) => {
        if (!e) return;
        saveAccess({
          account_id: accountId,
          user_id: userId,
          ft1_access: e.value as TFt1AccessChoice,
        })
          .unwrap()
          .then(() => toast.success(t("toast_saved")))
          .catch(onError);
      }}
      bottomLabel={
        data
          ? data.ft1_login_disabled
            ? t("user_status_blocked")
            : t("user_status_allowed")
          : undefined
      }
      disabled={!userCanEdit || isLoading}
      detailsFormField={true}
    />
  );
};
